import { useEffect } from 'react';
import { AppProvider, releaseSource, useApp, useRoute } from './app-state';
import { Icon, type IconName } from './components/Icon';
import { Attempt } from './screens/Attempt';
import { Home } from './screens/Home';
import { Lab } from './screens/Lab';
import { Practice } from './screens/Practice';
import { Progress } from './screens/Progress';
import { Session } from './screens/Session';
import { Settings } from './screens/Settings';
import { Setup } from './screens/Setup';

const TABS: { name: string; label: string; icon: IconName }[] = [
  { name: 'home', label: 'Train', icon: 'wave' },
  { name: 'progress', label: 'Progress', icon: 'chart' },
  { name: 'lab', label: 'Lab', icon: 'flask' },
  { name: 'settings', label: 'Settings', icon: 'settings' },
];

const FULLSCREEN = new Set(['setup', 'practice']);

function Shell() {
  const route = useRoute();
  const app = useApp();

  // The camera stays on only while a camera screen is open.
  useEffect(() => {
    if (!FULLSCREEN.has(route.name)) releaseSource();
  }, [route.name]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [route.name, route.id]);

  if (!app.ready) return null;

  let screen;
  switch (route.name) {
    case 'setup':
      screen = <Setup key={route.query.toString()} />;
      break;
    case 'practice':
      screen = <Practice key={route.query.toString()} />;
      break;
    case 'session':
      screen = <Session id={route.id!} />;
      break;
    case 'attempt':
      screen = <Attempt key={route.id} id={route.id!} />;
      break;
    case 'progress':
      screen = <Progress />;
      break;
    case 'lab':
      screen = <Lab />;
      break;
    case 'settings':
      screen = <Settings />;
      break;
    default:
      screen = <Home />;
  }

  const tab = route.name === 'session' || route.name === 'attempt' ? 'progress' : route.name;
  return (
    <div className="app">
      {screen}
      {!FULLSCREEN.has(route.name) && (
        <nav className="tabbar" aria-label="Main">
          {TABS.map((t) => (
            <a key={t.name} className="tab" href={`#/${t.name === 'home' ? '' : t.name}`} aria-current={tab === t.name ? 'page' : undefined}>
              <Icon name={t.icon} size={22} />
              {t.label}
            </a>
          ))}
        </nav>
      )}
    </div>
  );
}

export function App() {
  return (
    <AppProvider>
      <Shell />
    </AppProvider>
  );
}
